// app/javascript/controllers/p2p/confirm_modal_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Custom confirmation modal used by Turbo.setConfirmMethod.
 *
 * Enabled per-element via data-turbo-confirm-modal="true".
 * Supports custom title, message, confirm/cancel text and button classes.
 */
export default class extends Controller {
  static targets = [
    "overlay",
    "panel",
    "title",
    "message",
    "confirmButton",
    "cancelButton",
    "closeButton"
  ]

  static values = {
    title: String,
    message: String,
    confirmText: String,
    cancelText: String,
    confirmClass: String,
    cancelClass: String
  }

  currentResolve = null
  keydownHandler = null

  connect() {
    this.keydownHandler = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.removeListeners()
  }

  /**
   * Opens the modal and returns a Promise that resolves to true/false.
   *
   * @param {Object} config
   * @returns {Promise<boolean>}
   */
  open(config = {}) {
    return new Promise((resolve) => {
      this.currentResolve = resolve

      this.titleTarget.textContent = config.title || this.titleValue || "Подтвердите действие"
      this.messageTarget.textContent = config.message || this.messageValue || ""

      this.confirmButtonTarget.textContent = config.confirmText || this.confirmTextValue || "Принять"
      this.cancelButtonTarget.textContent = config.cancelText || this.cancelTextValue || "Отмена"

      this.applyButtonClasses(this.confirmButtonTarget, config.confirmClass || this.confirmClassValue)
      this.applyButtonClasses(this.cancelButtonTarget, config.cancelClass || this.cancelClassValue)

      this.element.classList.remove("hidden")
      document.addEventListener("keydown", this.keydownHandler)

      // Trigger enter animation on the next frame
      requestAnimationFrame(() => {
        this.panelTarget.classList.remove("scale-95", "opacity-0")
        this.panelTarget.classList.add("scale-100", "opacity-100")
      })

      this.confirmButtonTarget.focus()
    })
  }

  confirm() {
    this.resolve(true)
  }

  cancel() {
    this.resolve(false)
  }

  resolve(value) {
    if (this.currentResolve) {
      this.currentResolve(value)
      this.currentResolve = null
    }
    this.hide()
  }

  hide() {
    this.removeListeners()

    this.panelTarget.classList.remove("scale-100", "opacity-100")
    this.panelTarget.classList.add("scale-95", "opacity-0")

    const onTransitionEnd = (event) => {
      if (event.target !== this.panelTarget) return
      this.element.classList.add("hidden")
      this.panelTarget.removeEventListener("transitionend", onTransitionEnd)
    }

    this.panelTarget.addEventListener("transitionend", onTransitionEnd)

    // Fallback in case transitionend does not fire
    setTimeout(() => {
      this.element.classList.add("hidden")
      this.panelTarget.removeEventListener("transitionend", onTransitionEnd)
    }, 300)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    }
  }

  removeListeners() {
    document.removeEventListener("keydown", this.keydownHandler)
  }

  applyButtonClasses(button, classString) {
    // Reset to base classes, then apply the configured ones
    button.className = [
      "w-full sm:w-auto px-4 py-2 text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
      classString
    ].join(" ")
  }
}
